/**
 * Helpers for new lead campaigns with one Advantage+ leads campaign budget.
 *
 * Wizard state used here:
 *   budgetSchedule.budgetType       'advantage_leads' in this mode
 *   budgetSchedule.campaignBudget   daily campaign budget as typed (major units)
 *
 * In this mode Meta shares the campaign budget across the ad sets, chooses the placements, and the ad sets have no
 * budget of their own. The server offers the mode (switch on, lead campaign, no special ad categories) and checks it
 * again when the campaign is created.
 */
import { parseBudgetAmount, toMinorUnits } from '@/utils/audience-builder'

export const ADVANTAGE_LEADS_BUDGET_TYPE = 'advantage_leads'

export const ADVANTAGE_LEADS_LABEL = 'Campaign budget (Advantage+ leads)'

export const PLACEMENTS_NOTE =
    'Placements and devices are automatic in this mode: Meta chooses where the ads appear, so the placement and device choices of the audiences are not used.'

export const FAIR_SPLIT_NOTE =
    'Meta moves this campaign budget towards the ad sets that start well, so the results of the audiences are not a fair side-by-side comparison.'

export const ADVANTAGE_AUDIENCE_NOTE =
    'At least one audience must use Advantage+ audience: the AI Advantage+ audience or Broad Targeting.'

export const CAMPAIGN_BUDGET_AD_SET_NOTE = 'From the campaign budget'

export const ADVANTAGE_LEADS_ERROR_MESSAGES = {
    ADVANTAGE_LEADS_NOT_AVAILABLE:
        'Advantage+ leads campaigns are not available for this campaign any more. Choose another budget type.',
    INVALID_CAMPAIGN_BUDGET: 'Check the campaign budget: it must cover the minimum daily budget of every audience.',
    ADVANTAGE_AUDIENCE_REQUIRED: ADVANTAGE_AUDIENCE_NOTE,
}

/** Message for a launch or edit error caused by the campaign budget, or null when the error is something else. */
export const getAdvantageLeadsErrorMessage = (error) => {
    const code = error?.graphQLErrors?.[0]?.extensions?.code
    return ADVANTAGE_LEADS_ERROR_MESSAGES[code] || null
}

export const isAdvantageLeadsBudget = (budgetData) => budgetData?.budgetType === ADVANTAGE_LEADS_BUDGET_TYPE

/** Number of audiences (future ad sets) selected in the given stages. */
export const countAudiences = (audiencesByStage = {}, stages = Object.keys(audiencesByStage || {})) =>
    stages.reduce((count, stage) => count + (audiencesByStage?.[stage]?.length || 0), 0)

/**
 * Why the campaign budget cannot be used, or null. `amount` is in major units as typed; `minDailyBudget` is the ad
 * account minimum per ad set in minor units. Every ad set must still be able to get the minimum.
 */
export const campaignBudgetProblem = (amount, adSetCount, minDailyBudget) => {
    const value = parseBudgetAmount(amount)
    if (!value) return 'Enter the daily campaign budget.'
    const minor = toMinorUnits(value)
    if (Math.abs(minor - value * 100) > 1e-6) return 'Enter the budget in whole cents.'
    const count = Math.max(Number(adSetCount) || 0, 1)
    const minimum = (Number(minDailyBudget) || 0) * count
    if (minimum > 0 && minor < minimum) return { minimum }
    return null
}

/** The campaign budget fields of a new campaign, or null with ad set budgets. */
export const campaignBudgetForSubmission = (budgetData) => {
    if (!isAdvantageLeadsBudget(budgetData)) return null
    const amount = parseBudgetAmount(budgetData.campaignBudget)
    return { budget_mode: ADVANTAGE_LEADS_BUDGET_TYPE, daily_budget: amount ? toMinorUnits(amount) : 0 }
}

/**
 * Part of the campaign budget the AI audiences can count on (minor units): the campaign budget less the minimum of
 * every other ad set. Same rule as the server's audience budget gate.
 */
export const campaignBudgetShare = ({ campaignBudget, adSetCount, aiAdSetCount, minDailyBudget }) => {
    const others = Math.max((Number(adSetCount) || 0) - (Number(aiAdSetCount) || 0), 0)
    return Math.max((Number(campaignBudget) || 0) - (Number(minDailyBudget) || 0) * others, 0)
}

/**
 * Campaign budget fields of an edit (minor units): nothing when the budget is the one the edit was opened with, so a
 * budget changed elsewhere meanwhile is not set back; otherwise the new budget with the opened one to compare against.
 * Without a known opened budget the budget is always sent.
 */
export const campaignBudgetEditFields = (dailyBudget, openedDailyBudget) => {
    if (!Number.isFinite(openedDailyBudget)) return { daily_budget: dailyBudget }
    if (dailyBudget === openedDailyBudget) return {}
    return { daily_budget: dailyBudget, expected_daily_budget: openedDailyBudget }
}

/** Budget step data for a campaign edited or duplicated from one with a campaign budget (minor units stored). */
export const campaignBudgetStepData = (dailyBudget) => ({
    budgetType: ADVANTAGE_LEADS_BUDGET_TYPE,
    campaignBudget: dailyBudget != null ? String(Number(dailyBudget) / 100) : '',
})
